<script setup lang="ts">
  import { DocumentsDialog } from '@/components/app/documents'
  import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog'
  import { useTranslation } from '@/composables/useTranslation'
  import { ROUTES } from '@/router/routes'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import {
    AiChatSessionsService,
    AIChatSessionTypeEnum,
    type AIChatSession,
    type CustomerCase
  } from '@/types/generated'
  import {
    Bookmark,
    FilePlus2,
    Info,
    Loader2,
    Mic,
    Paperclip,
    SendHorizontal,
    Sparkles,
    Upload
  } from 'lucide-vue-next'
  import { computed, inject, nextTick, onMounted, provide, ref, watch, type Ref } from 'vue'
  import DocumentProcessingStatus from '../../cases/documents/DocumentProcessingStatus.vue'
  import PromptTemplatesDialog from './PromptTemplatesDialog.vue'

  const { t } = useTranslation({
    en: {
      YourMessage: 'Your message...',
      Generating: 'Generating...',
      SendMessage: 'Send message',
      AttachFile: 'Attach file',
      RecordVoice: 'Record voice message',
      PromptTemplates: 'Prompt templates',
      UploadDocuments: 'Upload documents',
      GenerateDocument: 'Generate document',
      EnhancePrompt: 'Enhance Prompt',
      Enhancing: 'Enhancing',
      UploadDocumentsToChat: 'Upload documents to the chat',
      AllUploadedDocumentsAttachedToChat: 'All uploaded documents are attached to this chat.'
    },
    de: {
      YourMessage: 'Ihre Nachricht...',
      Generating: 'Generiere...',
      SendMessage: 'Nachricht senden',
      AttachFile: 'Datei anhängen',
      RecordVoice: 'Sprachnachricht aufnehmen',
      PromptTemplates: 'Promptvorlagen',
      UploadDocuments: 'Dokumente hochladen',
      GenerateDocument: 'Dokument generieren',
      EnhancePrompt: 'Prompt verbessern',
      Enhancing: 'Verbessert',
      UploadDocumentsToChat: 'Dokumente in den Chat hochladen',
      AllUploadedDocumentsAttachedToChat:
        'Alle hochgeladenen Dokumente sind an diesen Chat angehängt.'
    }
  })

  // Inject case data and chat functionality
  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))
  const sessionType = inject<AIChatSessionTypeEnum>('sessionType', AIChatSessionTypeEnum.CASE)
  const sessionGroupData = inject<Ref<any>>('sessionGroupData', ref(null))
  const sendChatMessage = inject(
    'sendChatMessage',
    async (session: AIChatSession | null, message: string) => {}
  )
  const createNewChatSession = inject<(sessionGroupUuid?: string) => Promise<AIChatSession | null>>(
    'createNewChatSession',
    async () => null
  )
  const isSessionGenerating = inject<(uuid: string) => boolean>('isSessionGenerating', () => false)
  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))
  const isActiveSessionGenerating = computed(() => {
    return activeChatSession.value && isSessionGenerating(activeChatSession.value.uuid)
  })
  const userChatMessage = ref('')
  const textareaRef = ref<HTMLTextAreaElement | null>(null)

  // Reference to the status component for potential programmatic control
  const documentStatusRef = ref<InstanceType<typeof DocumentProcessingStatus> | null>(null)

  // Get stores for document processing data
  const caseStore = useCaseStore()
  const globalChatStore = useGlobalChatStore()

  // Get the appropriate document processing data based on session type
  const documentProcessingData = computed(() => {
    if (sessionType === AIChatSessionTypeEnum.CASE) {
      return {
        documents: caseStore.documentProcessor.documents || [],
        processingDocuments: caseStore.documentProcessor.processingDocuments || [],
        activeUploads: caseStore.documentProcessor.activeUploads || [],
        documentsWithErrors: caseStore.documentProcessor.documentsWithErrors || []
      }
    } else {
      // For global chat, combine both processors
      const chatDocs = globalChatStore.chatSessionProcessor?.documents || []
      const folderDocs = globalChatStore.folderProcessor?.documents || []
      const chatProcessing = globalChatStore.chatSessionProcessor?.processingDocuments || []
      const folderProcessing = globalChatStore.folderProcessor?.processingDocuments || []
      const chatUploads = globalChatStore.chatSessionProcessor?.activeUploads || []
      const folderUploads = globalChatStore.folderProcessor?.activeUploads || []
      const chatErrors = globalChatStore.chatSessionProcessor?.documentsWithErrors || []
      const folderErrors = globalChatStore.folderProcessor?.documentsWithErrors || []

      return {
        documents: [...chatDocs, ...folderDocs],
        processingDocuments: [...chatProcessing, ...folderProcessing],
        activeUploads: [...chatUploads, ...folderUploads],
        documentsWithErrors: [...chatErrors, ...folderErrors]
      }
    }
  })

  // ---------------
  // Chat functionality
  // ---------------

  const sendMessage = () => {
    if (userChatMessage.value.trim() && !isActiveSessionGenerating.value) {
      sendChatMessage(activeChatSession.value, userChatMessage.value.trim())
      userChatMessage.value = ''
      // Reset textarea height after sending
      nextTick(() => {
        adjustTextareaHeight()
      })
    }
  }

  // ---------------
  // Prompt Enhancement functionality
  // ---------------

  const isEnhancingPrompt = ref(false)
  const enhancementError = ref<string | null>(null)
  const showEnhanceTooltip = ref(false)

  const enhancePrompt = async () => {
    if (
      !userChatMessage.value.trim() ||
      isEnhancingPrompt.value ||
      isActiveSessionGenerating.value
    ) {
      return
    }

    isEnhancingPrompt.value = true
    enhancementError.value = null

    try {
      const response = await AiChatSessionsService.aiChatSessionsEnhancePromptCreate({
        requestBody: {
          content: userChatMessage.value.trim()
        }
      })

      // Replace the user's input with the enhanced prompt
      userChatMessage.value = response.enhanced_content
      // Adjust textarea height for the new content
      nextTick(() => {
        adjustTextareaHeight()
      })
    } catch (error) {
      console.error('Failed to enhance prompt:', error)
      enhancementError.value = 'Failed to enhance prompt. Please try again.'

      // Auto-hide error message after 5 seconds
      setTimeout(() => {
        enhancementError.value = null
      }, 5000)
    } finally {
      isEnhancingPrompt.value = false
    }
  }

  const isPromptPopoverOpen = inject<Ref<boolean>>('isPromptPopoverOpen', ref(false))
  const showUploadDocumentsDialog = ref(false)

  // Get documents based on context
  const documents = computed(() => {
    if (sessionType === AIChatSessionTypeEnum.CASE && caseData.value?.documents) {
      return caseData.value.documents
    } else if (sessionType === AIChatSessionTypeEnum.GLOBAL && sessionGroupData.value?.documents) {
      return sessionGroupData.value.documents
    }
    return []
  })

  // Get the appropriate document processor
  // For global context, create a temporary processor that will trigger session creation on file selection
  const documentProcessor = computed(() => {
    if (sessionType === AIChatSessionTypeEnum.CASE && caseData.value?.uuid) {
      return caseStore.documentProcessor
    } else if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
      // If we have an active session, use its processor
      if (activeChatSession.value?.uuid) {
        return globalChatStore.getSessionProcessor(activeChatSession.value.uuid)
      }
      // If no active session, return null - we'll handle session creation when files are selected
      return null
    }
    return null
  })

  // Handle file selection - create session if needed for global context
  const handleFilesSelected = async (files: File[]) => {
    // For global context without active session, create a new session first
    if (
      sessionType === AIChatSessionTypeEnum.GLOBAL &&
      !activeChatSession.value &&
      files.length > 0
    ) {
      try {
        // Get session group UUID if available
        const sessionGroupUuid = sessionGroupData.value?.uuid || undefined
        await createNewChatSession(sessionGroupUuid)
        // The documentProcessor will be recomputed now that we have an active session
        // Files will be uploaded once the session is created
      } catch (error) {
        console.error('Failed to create chat session for file upload:', error)
      }
    }
  }

  // Provide session type to child components
  provide('sessionType', sessionType)

  const closePromptPopover = () => {
    isPromptPopoverOpen.value = false
  }

  // Handle prefill from template
  const prefillFromTemplate = (content: string) => {
    userChatMessage.value = content
    // Adjust textarea height for the prefilled content
    nextTick(() => {
      adjustTextareaHeight()
    })
  }

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  // Computed property to determine if status should adjust input spacing
  const hasDocumentStatus = computed(() => {
    return documentStatusRef.value?.hasActivity || documentStatusRef.value?.showSuccessMessage
  })

  // Auto-resize textarea functionality
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.value
    if (!textarea) return

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto'

    // Calculate line height (approximately 1.25rem with leading-5)
    const lineHeight = 20 // 1.25rem = 20px
    const minRows = 3
    const maxRows = 9
    const minHeight = lineHeight * minRows
    const maxHeight = lineHeight * maxRows

    // Get the scroll height and constrain it
    const scrollHeight = textarea.scrollHeight
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight)

    // Set the new height
    textarea.style.height = `${newHeight}px`

    // Enable/disable scrolling based on whether we've reached max height
    if (scrollHeight > maxHeight) {
      textarea.style.overflowY = 'auto'
    } else {
      textarea.style.overflowY = 'hidden'
    }
  }

  // Watch for changes in userChatMessage to adjust height
  watch(userChatMessage, () => {
    nextTick(() => {
      adjustTextareaHeight()
    })
  })

  // Initialize textarea height on mount
  onMounted(() => {
    nextTick(() => {
      adjustTextareaHeight()
    })
  })
</script>

<template>
  <!-- Message Input -->
  <div
    class="mt-auto bg-gray-50 px-4 pb-4"
    data-cy="message-input-container"
  >
    <div class="mx-auto max-w-4xl">
      <!-- Document Processing Status - Now a reusable component -->
      <DocumentProcessingStatus
        ref="documentStatusRef"
        :documents="documentProcessingData.documents"
        :processing-documents="documentProcessingData.processingDocuments"
        :active-uploads="documentProcessingData.activeUploads"
        :documents-with-errors="documentProcessingData.documentsWithErrors"
        :auto-hide-success-after="3000"
        :show-only-when-active="false"
        :compact="false"
      />

      <!-- Enhancement Error Message -->
      <Transition name="fade">
        <div
          v-if="enhancementError"
          class="mb-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          <div class="flex items-center gap-2">
            <Info class="size-4 shrink-0" />
            <span>{{ enhancementError }}</span>
          </div>
        </div>
      </Transition>

      <div
        class="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
        :class="hasDocumentStatus ? 'mt-2' : 'mt-4'"
        data-cy="message-input-wrapper"
      >
        <textarea
          ref="textareaRef"
          v-model="userChatMessage"
          :disabled="!!isActiveSessionGenerating"
          class="block w-full resize-none overflow-y-auto border-0 pb-2 pe-4 ps-2 pt-2 leading-5 focus:outline-none
            focus:ring-0 disabled:pointer-events-none disabled:opacity-50 md:text-sm
            [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300
            [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar]:w-2"
          :placeholder="t('YourMessage')"
          rows="3"
          data-cy="chat-message-input"
          @keydown="handleKeydown"
          @input="adjustTextareaHeight"
        ></textarea>

        <div
          class="mx-2 my-2 flex items-center justify-between gap-x-1"
          data-cy="message-input-actions"
        >
          <!-- Left Button Group -->
          <div
            class="flex items-center gap-x-1"
            data-cy="left-button-group"
          >
            <div class="flex items-center gap-x-2">
              <!-- Prompt Templates Button with Popover -->
              <Dialog v-model:open="isPromptPopoverOpen">
                <DialogTrigger as-child>
                  <button
                    type="button"
                    :disabled="!!isActiveSessionGenerating"
                    class="prompt-template-btn relative flex items-center justify-center gap-x-1.5 overflow-hidden rounded-md
                      px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-0 disabled:pointer-events-none
                      disabled:opacity-50"
                    data-cy="prompt-templates-button"
                  >
                    <!-- Sleek, modern layered effects -->
                    <div class="btn-bg absolute inset-0 z-0 rounded-md"></div>
                    <div class="btn-border absolute inset-0 z-[1] rounded-md"></div>
                    <div class="subtle-glow absolute -inset-px z-[1] rounded-md opacity-0"></div>
                    <div
                      class="content-wrapper relative z-10 flex items-center justify-center gap-x-1.5"
                    >
                      <Bookmark class="icon-color size-4 shrink-0" />
                      <span class="text-color font-medium">{{ t('PromptTemplates') }}</span>
                    </div>
                  </button>
                </DialogTrigger>
                <DialogContent class="max-w-5xl overflow-hidden rounded-lg p-0">
                  <PromptTemplatesDialog
                    @close="closePromptPopover"
                    @prefill="prefillFromTemplate"
                  />
                </DialogContent>
              </Dialog>

              <!-- Upload Documents Button -->
              <!-- Show for case context or global context (even without active session) -->
              <DocumentsDialog
                v-if="
                  (sessionType === AIChatSessionTypeEnum.CASE && documentProcessor) ||
                  sessionType === AIChatSessionTypeEnum.GLOBAL
                "
                :document-processor="documentProcessor"
                :title="t('UploadDocumentsToChat')"
                :note="t('AllUploadedDocumentsAttachedToChat')"
                @files-selected="handleFilesSelected"
              >
                <template #trigger>
                  <button
                    type="button"
                    :disabled="!!isActiveSessionGenerating"
                    class="flex items-center justify-center gap-x-1.5 rounded-md border border-gray-300 px-2.5 py-1 text-sm
                      font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500
                      focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                    data-cy="upload-documents-button"
                  >
                    <Upload class="size-4 shrink-0" />
                    {{ t('UploadDocuments') }}
                  </button>
                </template>
              </DocumentsDialog>

              <!-- Document Generate Button -->
              <a
                v-if="caseData?.uuid"
                :href="`${ROUTES.APP.CASES.CASE_DETAIL.DOCUMENT_GENERATION.replace(':uuid', caseData.uuid)}`"
                class="flex items-center justify-center gap-x-1.5 rounded-md border border-gray-300 px-2.5 py-1 text-sm
                  font-medium text-gray-700 no-underline hover:bg-gray-50 focus:outline-none focus:ring-2
                  focus:ring-purple-500 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
              >
                <FilePlus2 class="size-4 shrink-0" />
                {{ t('GenerateDocument') }}
              </a>
            </div>

            <!-- Attach File Button (disabled/hidden) -->
            <button
              type="button"
              class="invisible flex size-8 items-center justify-center gap-x-1.5 rounded-full text-sm text-gray-600
                hover:bg-gray-100 focus:bg-gray-100 focus:outline-none disabled:pointer-events-none
                disabled:opacity-50"
            >
              <Paperclip class="size-4 shrink-0" />
              <span class="sr-only">{{ t('AttachFile') }}</span>
            </button>
          </div>

          <!-- Right Button Group -->
          <div
            class="flex items-center gap-x-1"
            data-cy="right-button-group"
          >
            <!-- Voice Record Button (disabled/hidden) -->
            <button
              type="button"
              class="invisible flex size-8 items-center justify-center gap-x-1.5 rounded-full text-sm text-gray-600
                hover:bg-gray-100 focus:bg-gray-100 focus:outline-none disabled:pointer-events-none
                disabled:opacity-50"
            >
              <Mic class="size-4 shrink-0" />
              <span class="sr-only">{{ t('RecordVoice') }}</span>
            </button>

            <!-- Enhance Prompt Button -->
            <div class="relative">
              <button
                type="button"
                :disabled="
                  isEnhancingPrompt || isActiveSessionGenerating || !userChatMessage.trim()
                "
                class="inline-flex size-8 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white
                  text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:bg-gray-50
                  focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                data-cy="enhance-prompt-button"
                @click="enhancePrompt"
                @mouseenter="showEnhanceTooltip = true"
                @mouseleave="showEnhanceTooltip = false"
              >
                <span
                  v-if="isEnhancingPrompt"
                  class="animate-spin"
                >
                  <Loader2 class="size-4" />
                </span>
                <span v-else>
                  <Sparkles class="size-4 shrink-0" />
                </span>
                <span class="sr-only">
                  {{ isEnhancingPrompt ? t('Enhancing') : t('EnhancePrompt') }}
                </span>
              </button>
              <!-- Custom Tooltip -->
              <Transition name="tooltip">
                <div
                  v-if="showEnhanceTooltip && !isEnhancingPrompt"
                  class="absolute bottom-full left-1/2 mb-2 -translate-x-1/2 transform whitespace-nowrap rounded bg-gray-900
                    px-2 py-1 text-xs text-white shadow-lg"
                >
                  {{ t('EnhancePrompt') }}
                  <div
                    class="absolute left-1/2 top-full -translate-x-1/2 transform border-4 border-transparent border-t-gray-900"
                  ></div>
                </div>
              </Transition>
            </div>
            <!-- End Enhance Prompt Button -->

            <!-- Send Button -->
            <button
              type="button"
              :disabled="!!isActiveSessionGenerating || !userChatMessage.trim()"
              class="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-purple-600 text-sm font-medium
                text-white hover:bg-purple-700 focus:bg-purple-700 focus:outline-none disabled:pointer-events-none
                disabled:opacity-50"
              data-cy="send-message-button"
              @click="sendMessage"
            >
              <span
                v-if="isActiveSessionGenerating"
                class="animate-spin"
              >
                <Loader2 class="size-4" />
              </span>
              <span v-else>
                <SendHorizontal class="size-5 shrink-0" />
              </span>
              <span class="sr-only">
                {{ isActiveSessionGenerating ? t('Generating') : t('SendMessage') }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- End Message Input -->
</template>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition:
      opacity 0.4s ease,
      transform 0.3s ease;
  }

  .fade-enter-from {
    opacity: 0;
    transform: translateY(-5px);
  }

  .fade-leave-to {
    opacity: 0;
    transform: translateY(5px);
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
    transform: translateY(0);
  }

  /* Sleek, Modern Prompt Template Button */
  .prompt-template-btn {
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 4px rgba(139, 92, 246, 0.2);
  }

  .prompt-template-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(139, 92, 246, 0.3);
  }

  /* Button background with gradient */
  .btn-bg {
    background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
    transition: all 0.3s ease;
  }

  /* Subtle border */
  .btn-border {
    border: 1px solid rgba(139, 92, 246, 0.3);
    transition: all 0.3s ease;
  }

  /* Subtle glow effect */
  .subtle-glow {
    box-shadow: 0 0 15px rgba(139, 92, 246, 0.4);
    opacity: 1;
    transition: opacity 0.3s ease;
  }

  /* Text and icon styling */
  .icon-color {
    color: white;
    transition: color 0.3s ease;
  }

  .text-color {
    color: white;
    transition: color 0.3s ease;
  }

  /* Subtle attention-drawing pulse animation */
  .prompt-template-btn::after {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 8px;
    border: 1px solid rgba(139, 92, 246, 0.4);
    opacity: 0;
    z-index: 0;
    animation: pulse-ring 3s cubic-bezier(0.455, 0.03, 0.515, 0.955) infinite;
  }

  @keyframes pulse-ring {
    0% {
      transform: scale(0.98);
      opacity: 0.4;
    }
    50% {
      opacity: 0;
    }
    100% {
      transform: scale(1.05);
      opacity: 0;
    }
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
</style>
